import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CheckSquare, Calendar, FileSignature, NotebookPen, Wand2, BookOpen, Video, Building2, ChevronRight, Layers, AlertTriangle } from "lucide-react";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

const FEATURE_META = {
  tasks: { label: "Tasks", icon: CheckSquare, to: "/dashboard/tasks", blurb: "Assigned & created tasks with deadlines", color: "amber" },
  meetings: { label: "Meetings", icon: Video, to: "/dashboard/meetings", blurb: "Schedule & join Jitsi calls", color: "pink" },
  documents: { label: "Documents", icon: FileSignature, to: "/dashboard/documents", blurb: "Write, share & export proposals", color: "indigo" },
  notes: { label: "Notes", icon: NotebookPen, to: "/dashboard/notes", blurb: "Personal scratchpad", color: "emerald" },
  ai: { label: "AI Content", icon: Wand2, to: "/dashboard/ai", blurb: "Generate copy with AI", color: "fuchsia" },
  team: { label: "My Team", icon: Layers, to: "/dashboard/team", blurb: "Manage direct reports & LOAs", color: "teal" },
  blogs: { label: "Blogs", icon: BookOpen, to: "/dashboard/blogs", blurb: "Author articles, drafts & posts", color: "rose" },
  schools: { label: "School Directory", icon: Building2, to: "/dashboard/schools", blurb: "Browse partner schools", color: "sky" },
  calendar: { label: "Social Calendar", icon: Calendar, to: "/dashboard/social-calendar", blurb: "Post pipeline across events", color: "violet" },
  events: { label: "Events", icon: Calendar, to: "/dashboard/events", blurb: "Manage active events", color: "orange" },
  profile: { label: "My Profile", icon: Sparkles, to: "/dashboard/profile", blurb: "Account & password", color: "slate" },
};

export default function CustomRoleDashboard() {
  const { user, customRole, loading } = useAuth();
  const [counts, setCounts] = useState({});

  // Always-on features (also live in /admin/features always_on response).
  const ALWAYS_ON = ["profile", "meetings"];
  const features = Array.from(new Set([...(customRole?.features || []), ...ALWAYS_ON]));

  useEffect(() => {
    // Lightweight stat preview — fetch only what the role can see, ignore errors.
    (async () => {
      const out = {};
      if (features.includes("tasks")) {
        try { out.tasks = (await api.get("/tasks")).data.length; } catch { /* ignore */ }
      }
      if (features.includes("meetings")) {
        try { out.meetings = (await api.get("/meetings")).data.length; } catch { /* ignore */ }
      }
      if (features.includes("documents")) {
        try { out.documents = (await api.get("/documents", { params: { kind: "doc" } })).data.length; } catch { /* ignore */ }
      }
      if (features.includes("blogs")) {
        try { out.blogs = (await api.get("/documents", { params: { kind: "blog" } })).data.length; } catch { /* ignore */ }
      }
      setCounts(out);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customRole?.role_key]);

  // Hide the always-on "profile" card from the grid (it's in the sidebar already)
  const gridFeatures = features.filter((f) => f !== "profile");

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <PageHeader
          title={`Welcome, ${user.name.split(" ")[0]}`}
          subtitle={customRole ? `Role: ${customRole.label} · Custom role created by Admin` : "Your personalised dashboard"}
          icon={Sparkles}
          accent="indigo"
        />

        {customRole && (
          <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-fuchsia-50 border border-indigo-100 rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="custom-role-banner">
            <Sparkles className="w-5 h-5 text-indigo-600 shrink-0" />
            <div className="text-sm text-indigo-900">
              <span className="font-bold">{customRole.label}</span>
              <span className="text-indigo-700"> · {features.length} module{features.length === 1 ? "" : "s"} enabled</span>
              <span className="text-indigo-500"> · meeting scope: {customRole.meeting_scope || "manager"}</span>
            </div>
          </div>
        )}

        {!customRole && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="custom-role-missing">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <div className="font-bold">Role configuration not loaded</div>
              <div>Your custom role doesn&apos;t have any modules enabled yet. Ask the Admin to assign features from the Custom Roles panel.</div>
            </div>
          </div>
        )}

        {/* Quick stats — only render counts we actually fetched */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(counts).map(([k, v]) => {
            const meta = FEATURE_META[k];
            if (!meta) return null;
            return <StatCard key={k} label={meta.label} value={v} icon={meta.icon} color={meta.color} />;
          })}
        </div>

        {/* Feature cards — one per enabled feature */}
        <h2 className="font-display font-black text-2xl mb-4">Your modules</h2>
        {gridFeatures.length === 0 ? (
          <div className="dashboard-card p-12 text-center text-slate-400">
            No modules enabled for this role yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gridFeatures.map((f, idx) => {
              const meta = FEATURE_META[f];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <motion.div
                  key={f}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                >
                  <Link to={meta.to} className="dashboard-card p-5 group hover:shadow-xl hover:-translate-y-0.5 transition-all block" data-testid={`feature-card-${f}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-11 h-11 rounded-xl bg-${meta.color}-100 text-${meta.color}-700 flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition" />
                    </div>
                    <div className="font-bold text-slate-900 mb-1">{meta.label}</div>
                    <div className="text-xs text-slate-500">{meta.blurb}</div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
